import * as React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useGhlCouples } from '@/hooks/useGoHighLevel'
import { GHL_OPPORTUNITY_STATUS_LABEL } from '@lib/enums/ghl-opportunity-status.enum'
import type { GhlCoupleDto } from '@lib/dto/gohighlevel.dto'
import { searchCouples } from '@lib/helpers/ghl-couple-search'

/** The most couples shown at once: the most recent, or the best matches for a search. */
const SHOWN = 10

interface GhlLeadPickerProps {
  venueId: string
  value: GhlCoupleDto | null
  onPick: (couple: GhlCoupleDto) => void
  onClear: () => void
  idPrefix: string
  /** Offer a way to the venue's GoHighLevel settings when no stages are chosen (organization admins). */
  canChooseStages: boolean
}

function leadDay(at: string): string {
  return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** "jane@example.com · Site Visit Booked (Open) · lead Sep 1, 2026 · Website · in Garden and Barn". */
export function coupleSummary(couple: GhlCoupleDto): string {
  const latest = couple.opportunities[0]
  const parts: string[] = []
  if (couple.email !== null) parts.push(couple.email)
  if (latest !== undefined) {
    parts.push(`${latest.stageName} (${GHL_OPPORTUNITY_STATUS_LABEL[latest.status]})`)
    parts.push(`lead ${leadDay(latest.leadCreatedAt)}`)
    if (latest.source !== null) parts.push(latest.source)
  }
  const places = [...new Set(couple.opportunities.map((o) => o.locationName))]
  if (places.length > 1) parts.push(`in ${places.slice(0, -1).join(', ')} and ${places.at(-1) ?? ''}`)
  return parts.join(' · ')
}

/**
 * Pick the couple an event is for from the venue's GoHighLevel leads — only the
 * couples in each connection's chosen stages who don't have an event yet, one
 * row per couple. Optional: the event can always be made without one.
 */
export function GhlLeadPicker({ venueId, value, onPick, onClear, idPrefix, canChooseStages }: GhlLeadPickerProps) {
  const couples = useGhlCouples(venueId, value === null)
  const [query, setQuery] = React.useState('')
  const settled = useDebouncedValue(query, 200)

  if (value !== null) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-line px-3 py-2">
        <div className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{value.name}</span>
          <span className="block truncate text-xs text-ink-muted">{coupleSummary(value)}</span>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClear}>
          Remove
        </Button>
      </div>
    )
  }

  if (couples.isPending) return <p className="m-0 text-sm text-ink-muted">Loading leads from GoHighLevel…</p>
  if (couples.isError) {
    return (
      <FormError>
        GoHighLevel can’t be reached right now. You can still create the event without it.
      </FormError>
    )
  }

  const { couples: all, truncated, unreachableConnections } = couples.data
  const searching = settled.trim() !== ''
  const matching = searchCouples(all, settled)
  const shown = matching.slice(0, SHOWN)

  return (
    <div className="space-y-3">
      {unreachableConnections.length > 0 ? (
        <Callout tone="attention">
          Leads from {unreachableConnections.map((c) => c.locationName).join(' and ')} can’t be shown right now.
        </Callout>
      ) : null}

      {all.length === 0 ? (
        <p className="m-0 text-sm text-ink-muted">
          No leads in the chosen stages.{' '}
          {canChooseStages ? (
            <Link to={`/venues/${venueId}/settings#gohighlevel`} className="font-medium text-ink underline underline-offset-2">
              Choose stages
            </Link>
          ) : null}
        </p>
      ) : (
        <>
          <Input
            id={`${idPrefix}-q`}
            aria-label="Search leads"
            placeholder="Search by name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          {shown.length === 0 ? (
            <p className="m-0 text-sm text-ink-muted">No leads match.</p>
          ) : (
            <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
              {shown.map((couple) => (
                <li key={couple.key} className="flex items-center gap-3 px-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{couple.name}</span>
                    <span className="block truncate text-xs text-ink-muted">{coupleSummary(couple)}</span>
                  </span>
                  <Button type="button" size="sm" variant="outline" onClick={() => onPick(couple)}>
                    Use
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {matching.length > SHOWN ? (
            <p className="m-0 text-xs text-ink-muted">
              Showing {String(SHOWN)} of {String(matching.length)}
              {truncated && !searching ? '+' : ''}. Search to find others.
            </p>
          ) : truncated ? (
            <p className="m-0 text-xs text-ink-muted">Only the 500 most recent leads are listed.</p>
          ) : null}
        </>
      )}
    </div>
  )
}
